import { contactsApi } from '@/api/queries/contacts';
import type { Contact } from '@/api/types/contacts';
import { Button, Icon, SectionCard, Tag, useToast } from '@/components/ui';

const STATUS_LABEL: Record<string, string> = {
  none: 'Not flagged',
  requiresReview: 'Requires review',
  reviewed: 'Reviewed',
  autoInvestigation: 'Auto Investigation',
  investigated: 'Investigated',
};

const statusTone = (status?: string): 'default' | 'amber' | 'green' =>
  status === 'requiresReview' || status === 'autoInvestigation'
    ? 'amber'
    : status === 'reviewed' || status === 'investigated'
      ? 'green'
      : 'default';

const verdictTone = (verdict: string): 'green' | 'amber' | 'red' =>
  verdict === 'ok' ? 'green' : verdict === 'bad' ? 'red' : 'amber';

/**
 * Review workflow panel for an existing contact. Lets a user flag the contact for
 * manual or automatic review, mark it reviewed, or clear the flag, and shows the
 * timeline of checks (the hourly ContactReviewJob records automatic checks here).
 */
export function ReviewSection({ contact }: { contact: Contact }) {
  const toast = useToast();
  const setReview = contactsApi.useSetReview();
  const id = contact.id ?? '';
  const review = contact.review;
  const status = review?.status ?? 'none';
  const checks = review?.checks ?? [];

  const apply = async (
    nextStatus: 'none' | 'requiresReview' | 'reviewed' | 'autoInvestigation' | 'investigated',
    mode?: 'manual' | 'automatic',
  ) => {
    await setReview.mutateAsync({ id, status: nextStatus, mode });
    toast.show({ title: 'Review status updated', severity: 'success' });
  };

  return (
    <SectionCard
      title="Review"
      actions={
        <Tag tone={statusTone(status)}>
          {STATUS_LABEL[status] ?? status}
          {review?.mode ? ` · ${review.mode}` : ''}
        </Tag>
      }
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icon name="shield" size={13} />}
          disabled={setReview.isPending}
          onClick={() => apply('requiresReview', 'manual')}
        >
          Flag for manual review
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icon name="shield" size={13} />}
          disabled={setReview.isPending}
          onClick={() => apply('requiresReview', 'automatic')}
        >
          Flag for automatic review
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icon name="search" size={13} />}
          disabled={setReview.isPending || status === 'autoInvestigation'}
          onClick={() => apply('autoInvestigation', 'automatic')}
        >
          Flag for auto investigation
        </Button>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Icon name="check" size={13} />}
          disabled={setReview.isPending || status === 'reviewed'}
          onClick={() => apply('reviewed', 'manual')}
        >
          Mark reviewed
        </Button>
        {status !== 'none' && (
          <Button
            variant="ghost"
            size="sm"
            disabled={setReview.isPending}
            onClick={() => apply('none')}
          >
            Clear
          </Button>
        )}
      </div>

      {checks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>
          {[...checks]
            .reverse()
            .map((c, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  fontSize: 12,
                  color: 'var(--text-2)',
                }}
              >
                <Tag tone={verdictTone(c.verdict)}>{c.verdict}</Tag>
                <div style={{ minWidth: 0 }}>
                  {c.message && <div>{c.message}</div>}
                  <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    {c.type} check
                    {c.by ? ` · ${c.by}` : ''}
                    {c.at ? ` · ${new Date(c.at).toLocaleString()}` : ''}
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </SectionCard>
  );
}
